// src/services/weeklyReportService.js
//
// Reads and writes for the patient-level weekly report (rules for what goes in
// the documents: weeklyReportModel.js). Only ever touches the three report
// collections below; never a patient record.
//
//   weekly_report_drafts/{uid}         the in-progress report (owner only)
//   weekly_reports/{id}                counts, as before (site staff)
//   weekly_report_patients/{id}        the patient list (admins + its coordinator)

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  getWeeklyReportDraftsPath,
  getWeeklyReportPatientsPath,
  getWeeklyReportsPath,
} from "./firestorePaths";
import { buildReportDocs, draftHasContent, normalizeDraft } from "./weeklyReportModel";

const toMs = (ts) => (ts?.toMillis ? ts.toMillis() : ts instanceof Date ? ts.getTime() : 0);

/** The signed-in user's saved draft, or null. */
export const loadDraft = async (uid) => {
  if (!uid) return null;
  const snap = await getDoc(doc(db, getWeeklyReportDraftsPath(), uid));
  if (!snap.exists()) return null;
  const data = snap.data() || {};
  return { draft: normalizeDraft(data), updatedAtMs: toMs(data.updatedAt) };
};

export const saveDraft = async (uid, draft) => {
  if (!uid) return;
  await setDoc(doc(db, getWeeklyReportDraftsPath(), uid), {
    ...normalizeDraft(draft),
    updatedAt: serverTimestamp(),
  });
};

export const deleteDraft = async (uid) => {
  if (!uid) return;
  await deleteDoc(doc(db, getWeeklyReportDraftsPath(), uid));
};

/**
 * Submit: the counts doc, the patient-list doc (same id) and the draft
 * deletion in ONE batch, so they save together or not at all.
 */
export const submitReport = async ({ uid, authorEmail, draft, segment, coordinator, coordinatorEmail }) => {
  const { reportDoc, patientsDoc } = buildReportDocs({
    draft,
    segment,
    coordinator,
    coordinatorEmail,
    authorUid: uid,
    authorEmail,
  });
  // A report may be only pre-screens not tied to a study (no studies at all).
  if (!draftHasContent(draft)) throw new Error("Add a patient, a follow-up number or pre-screens before submitting.");
  const reportRef = doc(collection(db, getWeeklyReportsPath()));
  const batch = writeBatch(db);
  batch.set(reportRef, { ...reportDoc, submittedAt: serverTimestamp() });
  batch.set(doc(db, getWeeklyReportPatientsPath(), reportRef.id), {
    ...patientsDoc,
    reportId: reportRef.id,
    createdAt: serverTimestamp(),
  });
  batch.delete(doc(db, getWeeklyReportDraftsPath(), uid));
  await batch.commit();
  return reportRef.id;
};

const shapePatientsDoc = (d) => {
  const data = d.data() || {};
  return { id: d.id, ...data, createdAtMs: toMs(data.createdAt) };
};

/**
 * Reports this user may see as "theirs": ones they filed, and ones an admin
 * filed for them (matched on their login email). Newest first.
 */
export const loadMyReportPatients = async ({ uid, email }) => {
  const reads = [getDocs(query(collection(db, getWeeklyReportPatientsPath()), where("authorUid", "==", uid)))];
  const lower = String(email || "").toLowerCase();
  if (lower) {
    reads.push(
      getDocs(query(collection(db, getWeeklyReportPatientsPath()), where("coordinatorEmail", "==", lower)))
    );
  }
  const snaps = await Promise.all(reads);
  const byId = new Map();
  snaps.forEach((s) => s.docs.forEach((d) => byId.set(d.id, shapePatientsDoc(d))));
  return [...byId.values()].sort((a, b) => b.createdAtMs - a.createdAtMs);
};

/**
 * Admins only (by rule): every report FOR one coordinator, whoever filed it —
 * the coordinator themselves or an admin on their behalf. Matched on the
 * coordinator's name and, when the roster has one, their email. Newest first.
 */
export const loadReportsForCoordinator = async ({ name, email }) => {
  const col = collection(db, getWeeklyReportPatientsPath());
  const reads = [];
  if (String(name || "").trim()) reads.push(getDocs(query(col, where("coordinator", "==", String(name).trim()))));
  const lower = String(email || "").toLowerCase();
  if (lower) reads.push(getDocs(query(col, where("coordinatorEmail", "==", lower))));
  const snaps = await Promise.all(reads);
  const byId = new Map();
  snaps.forEach((s) => s.docs.forEach((d) => byId.set(d.id, shapePatientsDoc(d))));
  return [...byId.values()].sort((a, b) => b.createdAtMs - a.createdAtMs);
};

/** Every patient-level report on the site (admins only, by rule). Newest first. */
export const loadAllReportPatients = async () => {
  const snap = await getDocs(collection(db, getWeeklyReportPatientsPath()));
  return snap.docs.map(shapePatientsDoc).sort((a, b) => b.createdAtMs - a.createdAtMs);
};
